import cx from 'classnames'
import DOMPurify from 'dompurify'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { DOMOutputSpecArray, DOMSerializer } from 'prosemirror-model'
import { MouseEvent, forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { ArticleClient } from 'api/clients/ArticleClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { ReactComponent as DocIcon } from 'assets/icons/document.svg'
import { InfoTooltip } from 'common/InfoTooltip/InfoTooltip'
import { useReactNodeView } from 'common/Prose/nodeViews/ReactNodeView/ReactNodeViewContext'
import { SmartLinkAttributes, SmartLinkDataAttributes } from 'common/Prose/utils/smartLinks/types'
import { COMMENT_VIEW } from 'components/DocRightRail/Constants'
import { handbookLink } from 'components/Handbook2_0/util'
import { MENTION_FORMAT_BAR_ID } from 'components/MentionsFormatBar/MentionsFormatBar'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import store from 'redux/store'
import { useRootStore } from 'stores/RootStore'
import { Permissions } from 'types'
import { ourUuid } from 'utils'
import { hasModifierKey } from 'utils/hasModifierKey'
import { getEntityIdFromCurrentUrl } from 'utils/urlStoreUtils'

import { EditableSmartLink } from './components/EditableSmartLink/EditableSmartLink'

import Styles from './MentionSmartLink.module.scss'

const SHOW_DELAY = 500
const HIDE_DELAY = 500
let menuEnterTimer: NodeJS.Timeout | null
let menuLeaveTimer: NodeJS.Timeout | null

export const MentionSmartLink = observer(
  forwardRef<HTMLSpanElement, any>(() => {
    const nodeViewContext = useReactNodeView()
    const { node, view, getPos } = nodeViewContext
    const history = useHistory()
    const [isUserSidebarOpen, setIsUserSidebarOpen] = useState<boolean>(false)
    const [hoverShowFormatBar, setHoverShowFormatBar] = useState<boolean>(false)
    const [forceShowFormatBar, setForceShowFormatBar] = useState<boolean>(false)
    const [permissionLevel, setPermissionLevel] = useState<Permissions | null | undefined>()
    const attrs = node?.attrs as SmartLinkAttributes
    const { key, title, type, userId, url } = attrs
    const containerRef = useRef<HTMLSpanElement>(null)
    const isSelected = nodeViewContext.isSelected ?? false
    const rootStore = useRootStore()

    const smartLinkUuid = useMemo(
      () => ourUuid(),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [node?.attrs],
    )

    useEffect(() => {
      setForceShowFormatBar(isSelected)
    }, [isSelected])

    const currentHandbook = () => {
      const state = store.getState()
      return state.handbook
    }

    const getHandbookSiblingArticles = () => {
      const currentArticleId = getEntityIdFromCurrentUrl()
      if (!currentArticleId) return []

      const handbook = currentHandbook()
      if (!handbook) return []

      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return handbook.items.filter((i) => i.document).map((i) => i.document!.key)
    }

    const isInternalHandbookLink = () => {
      return type === 'Article' && !!key && getHandbookSiblingArticles().includes(key)
    }

    const handleLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
      if (!hasModifierKey(event) && isInternalHandbookLink()) {
        event.preventDefault()
        const pathname = new URL(event.currentTarget.href).pathname
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const newHistory = isInternalHandbookLink() && attrs.key ? handbookLink(currentHandbook()!, key) : pathname
        history.push(newHistory)
      }
    }

    let dataProps: SmartLinkDataAttributes = {
      'data-type': type,
      'data-title': title,
      'data-url': url,
      'data-uuid': smartLinkUuid,
    }
    if (type === 'Article' || type === 'Handbook') {
      dataProps['data-key'] = key
    }

    if (type === 'User') {
      dataProps['data-user-id'] = userId
    }

    const showEditableSmartLink = () => !!getPos && view?.editable === true

    const commentId: number | undefined = computed(() => {
      if (view?.state.doc && node?.content && node.content.content && node.content.content.length > 0) {
        const commentMark = node.content.content[0].marks.find((m) => m.type.name === 'comment')
        if (commentMark?.attrs.uuid) {
          return rootStore.comments.getByUuid(commentMark.attrs.uuid)?.id
        }
      }
    }).get()

    // TODO: Unify isCommentSelected into a hook
    const { isMobile } = useResponsiveDesign()
    const isCommentsRailVisible = computed(() => {
      return Boolean(
        rootStore.docStore?.rightRailStore.locked && rootStore.docStore?.rightRailStore.activeTab === COMMENT_VIEW,
      )
    }).get()
    const isCommentSelected = computed(() => {
      return (isMobile || isCommentsRailVisible) && commentId != null && commentId === rootStore.comments.selected?.id
    }).get()

    const handleTouch = () => {
      if (commentId) rootStore.comments.select(commentId)
    }

    const anchorContent = useMemo(() => {
      const origin = document.createElement('span')
      let wrapper = origin
      const ignoreMarks = ['strong']
      if (view?.state.doc && node?.content && node.content.content && node.content.content.length > 0) {
        const marks = node.content.content[0].marks.filter((m) => !ignoreMarks.includes(m.type.name))
        if (marks && marks.length > 0) {
          marks.forEach((mark) => {
            const domSpec = mark.type.spec.toDOM?.(mark, true) as DOMOutputSpecArray
            const tag = DOMSerializer.renderSpec(document, domSpec)
            wrapper.append(tag.dom)
            wrapper = tag.dom as HTMLElement
          })
        }
      }
      wrapper.textContent = title

      return origin.innerHTML
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node?.content, title])

    const sanitizedAnchor = useMemo(() => {
      return {
        __html: DOMPurify.sanitize(anchorContent),
      }
    }, [anchorContent])

    const handleMouseOverFormatBar: EventListener = (event) => {
      if (
        menuLeaveTimer &&
        event.target &&
        event.target instanceof Element &&
        event.target.closest(`[data-smart-link-uuid='${smartLinkUuid}']`)
      ) {
        clearTimeout(menuLeaveTimer)
        menuLeaveTimer = null
      }
      document.getElementById(MENTION_FORMAT_BAR_ID)?.addEventListener('mouseleave', handleMouseLeaveFormat)
      document.getElementById(MENTION_FORMAT_BAR_ID)?.removeEventListener('mouseenter', handleMouseOverFormatBar)
    }

    const handleMouseLeaveFormat = () => {
      if (menuEnterTimer) {
        clearTimeout(menuEnterTimer)
        menuEnterTimer = null
      }
      menuLeaveTimer = setTimeout(() => {
        setHoverShowFormatBar(false)
      }, HIDE_DELAY)
    }

    const handleMouseLeave = (event: Event) => {
      if (menuLeaveTimer) return
      if (menuEnterTimer) {
        clearTimeout(menuEnterTimer)
        menuEnterTimer = null
      }
      menuLeaveTimer = setTimeout(() => {
        setHoverShowFormatBar(false)
        menuLeaveTimer = null
      }, HIDE_DELAY)
    }

    const handleMouseEnter = () => {
      determinePermissions()
      if (showEditableSmartLink()) {
        if (menuEnterTimer) return
        containerRef.current?.removeEventListener('mouseleave', handleMouseLeave)
        if (menuLeaveTimer && document.querySelector(`[data-smart-link-uuid='${smartLinkUuid}']`)) {
          clearTimeout(menuLeaveTimer)
          menuLeaveTimer = null
        }

        menuEnterTimer = setTimeout(() => {
          setHoverShowFormatBar(true)
          document.getElementById(MENTION_FORMAT_BAR_ID)?.addEventListener('mouseenter', handleMouseOverFormatBar)
          menuEnterTimer = null
        }, SHOW_DELAY)
        containerRef.current?.addEventListener('mouseleave', handleMouseLeave)
      }
    }

    const determinePermissions = () => {
      if (type !== 'Article' || typeof permissionLevel !== 'undefined' || !key) {
        return
      }

      ArticleClient.getArticle(key).then((response) => {
        if (response.type === 'error' && response.status === 403) {
          setPermissionLevel(null)
        }
        if (response.type === SUCCESS_RESPONSE_TYPE) {
          const { current_user_access } = response.data.article
          setPermissionLevel(current_user_access)
        }
      })
    }

    useEffect(() => {
      if (!isSelected) {
        setIsUserSidebarOpen(false)
      }
    }, [isSelected])

    if ((!key && !userId) || !view || !node || !getPos) return null

    return (
      <span
        className={cx(Styles.link, {
          [Styles.selected]: isSelected,
          [Styles.hasComment]: !isSelected && commentId,
          [Styles.commentSelected]: !isSelected && isCommentSelected,
        })}
        data-uuid={smartLinkUuid}
        onMouseEnter={handleMouseEnter}
        onClick={handleTouch}
        ref={containerRef}
      >
        {showEditableSmartLink() ? (
          <EditableSmartLink
            sanitizedHTML={sanitizedAnchor}
            nodeViewContext={nodeViewContext}
            dataProps={dataProps}
            displayTitle={title}
            getPos={getPos}
            handleLinkClick={handleLinkClick}
            parentRef={containerRef}
            isUserSidebarOpen={isUserSidebarOpen}
            setIsUserSidebarOpen={setIsUserSidebarOpen}
            showMentionFormatBar={hoverShowFormatBar || forceShowFormatBar}
            setShowMentionFormatBar={(show: boolean) => {
              setForceShowFormatBar(show)
              if (!show) {
                setHoverShowFormatBar(show)
              }
            }}
          />
        ) : (
          <InfoTooltip
            className={cx(Styles.tooltip, { [Styles.disabledTooltip]: permissionLevel !== null })}
            placement="bottom"
            description="You do not have access to this doc."
          >
            <span>
              {type === 'Article' && <DocIcon className={Styles.leadingIcon} />}
              <a
                data-test-id="smart-link-read-only"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                {...dataProps}
                onClick={handleLinkClick}
                tabIndex={-1}
                dangerouslySetInnerHTML={sanitizedAnchor}
              />
            </span>
          </InfoTooltip>
        )}
      </span>
    )
  }),
)
